<template>
  <!-- A focused card fills its 2x2 grid area so the gallery has leftover height to absorb. -->
  <div
    class="relative f-col"
    :class="{ 'h-full': isFocused }"
    data-test-id="product-card"
  >
    <base-button
      v-if="!isComingSoon && !isRobisOnly && !isDropProduct"
      :aria-describedby="product.id"
      :aria-label="$t.add"
      class="group absolute right-0 top-0 z-3 p-1"
      data-test-id="product-card-add"
      @click="openQuickshop"
    >
      <span class="block rounded-xs group-hocus:bg-white group-hocus:bg-op-90 p-1 group-hocus:forced-colors:outline duration lg:m-2 forced-colors:bg-[ButtonFace]">
        <vf-icon :height="{ sm: '20', lg: '24' }" name="plus" :width="{ sm: '20', lg: '24' }" />
      </span>
    </base-button>
    <div v-if="galleryPending" class="w-full skeleton" style="aspect-ratio: 4/5" />
    <product-card-gallery
      v-else
      :asset-override
      :fetchpriority
      :focused
      :items="activeGallery"
      :lazy
      :name="data.name"
      show-secondary-media
    >
      <template #link>
        <base-link aria-hidden="true" class="absolute-0" tabindex="-1" :to="url" @click.capture="navigateToPDP" />
      </template>
      <template #badge>
        <slot name="badge" />
      </template>
    </product-card-gallery>
    <div class="p-2 space-y-1" :class="{ relative: $feature.enableProductCardGalleryCarousel }">
      <div v-if="showEyebrow" class="line-clamp-1 eyebrow" data-test-id="product-card-badge" style="height: 0.96rem">
        {{ data.eyebrow?.label }}
      </div>
      <base-link
        :id="product.id"
        class="line-clamp-2 justify-self-start body-2"
        data-test-id="product-card-title"
        :to="url"
        @click.capture="navigateToPDP"
      >
        <span class="absolute-0" />
        {{ data.name }}
      </base-link>
      <div v-if="$feature.showShortDescriptionInProductList && data.shortDescription" class="body-4 c-grey-30">
        {{ data.shortDescription }}
      </div>
      <span
        v-if="!hideRating && data?.rating?.reviewsCount"
        :aria-label="replaceAll($t.ratingLabel, { value: data?.rating?.score?.toFixed(1), max: 5 })"
        class="items-center gap-0.5 pb-2 c-grey-30 i-flex"
      >
        <span class="text-xs">{{ data?.rating?.score?.toFixed(1) }}</span>
        <vf-icon aria-hidden="true" name="star-filled" />
      </span>
      <base-link v-if="data.customsExternalUrl" class="relative body-4 underlined" :to="data.customsExternalUrl">
        <span class="absolute-0" />
        {{ $t.customize }}
      </base-link>
      <span v-else-if="colorsCount" class="flex body-4 c-grey-30">
        {{ colorsCount > 15 ? '15+' : colorsCount }} {{ $pluralize($t.colors, colorsCount).toLowerCase() }}
      </span>
      <product-pricing
        class="body-4"
        :currency="data.currency"
        data-test-id="product-card-pricing"
        :price="productPrice"
        :show-discount-percentage
      />
    </div>

    <panel-quickshop
      class-wrapper="!p-0"
      :size="{ sm: 'sm', lg: 'full' }"
      :title="$t.size"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Emits, Props } from '#commerce/components/product/card/Card.vue'
import type { Product } from '#root/api/clients/product/data-contracts'
import type { ProductClickAction } from '#types/gtm'
import type { ColorOption } from '#types/product'
import { GalleryContextKey } from '#commerce/contexts/gallery'

const {
  breadcrumbs = [],
  focused,
  imageFetchPriority: fetchpriority,
  trackingOffset,
  product,
  storeFilterState,
  trackingCategory,
  trackingList,
} = defineProps<Props>()
const emit = defineEmits<Emits>()

const { isFocused } = useProductCardMedia(() => focused)

const { $feature, $gtm } = useNuxtApp()
const origin = useOrigin()
const route = useRoute()
const router = useRouter()
const { getIsComingSoonProduct } = useComingSoon()

const PanelQuickshop = createPanel('quickshop')

const productUrl = ref(product.url)
const defaultColor = getDefaultColorOption(product.attributes, product.id) || {}
const swatchId = ref(defaultColor.id || product.id)

const showEyebrow = computed(() => {
  const { mode, routes } = $feature.eyebrowVisibilityOnRoute
  const match = routes.some((pattern) => convertPatternToRegex(pattern).test(route.path))
  return mode === 'show' ? match : !match
})

const productDetailsKey = computed(() => `productDetails-${swatchId.value}`)
const { data } = useApi().products.details(swatchId, {}, {
  immediate: false,
  key: productDetailsKey,
  default: () => ({
    ...product,
    url: defaultColor.url || productUrl.value,
    eyebrow: defaultColor.eyebrow || product.eyebrow,
    badge: defaultColor.badge || product.badge
  }),
  transform: (input) => ({
    ...input,
    eyebrow: formatBadgeOrEyebrow(
      input.eyebrow || product.eyebrow,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    ),
    badge: formatBadgeOrEyebrow(
      input.badge || product.badge,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )
  })
})

const galleryContextKey = inject(GalleryContextKey)
const selectedColor = computed(() => {
  const options = product.attributes?.find(({ type }) => type.toLowerCase() === 'color')?.options as ColorOption[] | undefined
  return options?.find(({ id }) => id === swatchId.value)
})

const { activeGallery, galleryPending } = useActiveGallery(computed(() => ({
  id: swatchId.value,
  gallery: selectedColor.value?.gallery || product.gallery || data.value?.gallery,
  altGalleries: selectedColor.value?.altGalleries
    || (product as Product).altGalleries
    || (data.value as Product | undefined)?.altGalleries
})), galleryContextKey)

const categoryURL = breadcrumbs[breadcrumbs.length - 1]?.url
const colorsCount = 'availableColorsCount' in product ? product.availableColorsCount : 0
const url = computed(() => {
  if (data.value?.url && storeFilterState) {
    const baseUrl = `${data.value.url.startsWith('/') ? origin : ''}${data.value.url}`
    const parsedUrl = new URL(baseUrl)

    parsedUrl.searchParams.append('storeFilter', storeFilterState)

    return `${parsedUrl.pathname}${parsedUrl.search}`
  }

  return data.value?.url
})

const isComingSoon = computed(() => $feature.showComingSoonOnQuickshop
  && getIsComingSoonProduct(data.value.badge, data.value.eyebrow))
const isRobisOnly = computed(() => $feature.allowProductReservationOnPDP && data.value?.isRobisOnly)
const isDropProduct = computed(() => url.value && /^https?:\/\//.test(url.value))

const productPrice = computed(() => {
  if (!$feature.enablePlpSwatchDetails || data.value.displayPriceRange) return data.value.price

  const swatches = data.value?.attributes.find(({ type }) => type === 'color')?.options as ColorOption[]
  const colorSwatch = swatches?.find(({ id }) => id === swatchId.value) as ColorOption
  return colorSwatch?.price || data.value.price
})

const handleGtmEvent = (payload, action: ProductClickAction) =>
  $gtm.push('product.onProductClick', payload, {
    action,
    breadcrumbs,
    category: trackingCategory,
    list: trackingList,
    offset: trackingOffset,
    searchTerm: route.query.q?.toString()
  })

const navigateToPDP = (e?: PointerEvent) => {
  // Product drop cards will link out to a separate site
  if (isDropProduct.value) return

  handleGtmEvent(data.value, 'Navigate To PDP')
  emit('product-click', product)

  const serializedBreadcrumbs = serializeAnalyticsBreadcrumbs(breadcrumbs)
  const state = {
    breadcrumbs: serializedBreadcrumbs,
    category: trackingCategory,
    list: trackingList,
    searchTerm: route.query.q,
    categoryURL,
    galleryContext: galleryContextKey?.value
  }

  if (e?.metaKey || e?.ctrlKey) {
    localStorage.setItem(`from_plp_${data.value.id}`, JSON.stringify(state))
  }
  else {
    e?.preventDefault()
    router.push({
      path: url.value.split('?')[0],
      query: Object.fromEntries(new URLSearchParams(url.value.split('?')[1])),
      state
    })
  }
}

const openQuickshop = () => {
  PanelQuickshop.open({
    product: data.value as Product,
    productId: swatchId.value,
    categoryURL,
    gtmConfig: {
      ...(trackingCategory && { category: trackingCategory }),
      ...(trackingList && { list: trackingList }),
      ...(storeFilterState && { shippingMethod: 'BOPIS_PLP_SD', shippingStoreId: storeFilterState, shippingWindow: '24Hrs' })
    }
  })
  handleGtmEvent(data.value, 'Open Quick Shop')
}
</script>
